import { useDisclosure } from "@mantine/hooks"
import { InformationRequestStatus } from "@tfg/db"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { Navigate } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { useSetStatusRequestMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks"
import { RequestWithOpItemFromOperationTree } from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { useAuth } from "~/store/auth-context"

type InformationRequestContextType = {
  status: InformationRequestStatus
  user: LibraryResponses.TFGUser
  isAdmin: boolean
  canSubmitIR: boolean
  request: RequestWithOpItemFromOperationTree
  currentContactId: string
  progressByOperationId: Record<number, number>
  customFieldValues: TraceabilityResponses.CustomFieldValue[]
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
  setProgress: (operationId: number, value: number) => void
  handleSetStatusOpen: (status: InformationRequestStatus) => void
  handleReopenClick: (mode: "clean" | "dirty") => void
  reopened: boolean
  reopenRequestMode: "clean" | "dirty"
  reopenClose: () => void
}

type InformationRequestContextProps = {
  children: ReactNode
  request: RequestWithOpItemFromOperationTree
}

const InformationRequestContext = createContext<
  InformationRequestContextType | undefined
>(undefined)

export function InformationRequestContextProvider({
  children,
  request,
}: InformationRequestContextProps) {
  const { user, isAdmin } = useAuth()
  const [reopenRequestMode, setReopenRequestMode] = useState<"clean" | "dirty">(
    "clean"
  )
  const [reopened, reopenHandlers] = useDisclosure(false)
  const { mutate: setStatusOpen } = useSetStatusRequestMutation()

  const handleSetStatusOpen = useCallback(
    (newStatus: InformationRequestStatus) => {
      setStatusOpen({
        requestId: request.original.id,
        status: newStatus,
      })
    },
    [request.original.id, setStatusOpen]
  )

  const handleReopenClick = useCallback(
    (mode: "clean" | "dirty") => {
      setReopenRequestMode(mode)
      reopenHandlers.open()
    },
    [reopenHandlers]
  )

  const [progressByOperationId, setProgressByOperationId] = useState<
    Record<number, number>
  >(
    request.opItems.reduce(
      (acc, item) => {
        if (!item.operation) {
          return acc
        }
        acc[item.operation.id] = item.operationCompletionStatus?.progress ?? 0
        return acc
      },
      {} as Record<number, number>
    )
  )
  const currentContactId = request.original.contactId

  const setProgress = useCallback(
    (operationId: number, value: number) => {
      setProgressByOperationId((prev) => ({
        ...prev,
        [operationId]: value,
      }))
    },
    [setProgressByOperationId]
  )
  const [customFieldValues, setCustomFieldValues] = useState<
    TraceabilityResponses.CustomFieldValue[]
  >(
    request.opItems.flatMap((item) => {
      if (!item.operation) {
        return []
      }
      return item.operation.customFieldsValues
    })
  )

  useEffect(() => {
    setCustomFieldValues(
      request.opItems.flatMap((item) => {
        if (!item.operation) {
          return []
        }
        return item.operation.customFieldsValues
      })
    )
  }, [request.opItems])

  if (!user) {
    return <Navigate to="/login" replace={true} />
  }

  const canSubmitIR = Object.values(progressByOperationId).every(
    (progress) => progress === 100
  )

  const value: InformationRequestContextType = {
    status: request.original.status,
    user,
    isAdmin,
    request,
    currentContactId,
    canSubmitIR,
    customFieldValues,
    setCustomFieldValues,
    setProgress,
    progressByOperationId,
    reopenRequestMode,
    reopened,
    handleSetStatusOpen,
    handleReopenClick,
    reopenClose: reopenHandlers.close,
  }

  return (
    <InformationRequestContext.Provider value={value}>
      {children}
    </InformationRequestContext.Provider>
  )
}

export function useInformationRequestContext() {
  const context = useContext(InformationRequestContext)
  if (!context) {
    throw new Error(
      "useInformationRequestContext must be used within an InformationRequestContextProvider"
    )
  }
  return context
}
